import { RETRACED_ROLE_KEYS } from '../permissions/index.js';

/**
 * Which development tags a user with a given tag can see features for.
 * Ports `DEVELOPMENT_TAGS_HIERARCHY` from
 * `backend/src/2025-06/api-service/routes/schemas/v3/constants.js`.
 */
export const DEVELOPMENT_TAGS_HIERARCHY: Record<string, string[]> = {
  EXPERIMENTAL: ['EXPERIMENTAL', 'ALPHA', 'BETA'],
  ALPHA: ['ALPHA', 'BETA'],
  BETA: ['BETA'],
};

/**
 * Retraced-staff roles (as opposed to company-scoped roles). Derived from the
 * canonical `RETRACED_ROLE_KEYS` (`modules/permissions`) so this list can't
 * drift from what the retraced tenant's `iam_roles` actually contains.
 */
export const RETRACED_ADMIN_ROLES: readonly string[] =
  Object.keys(RETRACED_ROLE_KEYS);

/**
 * Valid `developmentTag` values a user can be assigned. Ports
 * `USER_DEVELOPMENT_TAGS` from
 * `backend/src/2025-06/api-service/routes/schemas/v3/constants.js`.
 */
export const USER_DEVELOPMENT_TAGS = ['EXPERIMENTAL', 'ALPHA', 'BETA'] as const;

export type UserDevelopmentTag = (typeof USER_DEVELOPMENT_TAGS)[number];

/**
 * Display names of the development tags. Ports `DEVELOPMENT_TAGS` from
 * `backend/src/2025-06/api-service/routes/schemas/v3/constants.js`.
 */
export const DEVELOPMENT_TAG_NAMES: Record<UserDevelopmentTag, string> = {
  EXPERIMENTAL: 'Experimental',
  ALPHA: 'Alpha',
  BETA: 'Beta',
};

/**
 * Permission keys guarding the admin (staff) user management routes
 * (`backend/src/api/routes/admin/users/*`). Shared so the route guards and
 * any code computing the equivalent capability for display (e.g.
 * `UserDetailService`) can't drift apart on the literal string.
 */
export const ADMIN_USERS_PERMISSIONS = {
  CREATE: 'admin-users:create',
  DELETE: 'admin-users:delete',
} as const;

/**
 * Platform feature id that, when enabled for a company, lets its members see
 * email (and a few other sensitive fields) of users from OTHER companies in
 * the scoped users list. Ports `PLATFORM_FEATURES_ENUM.VIEW_NETWORK_USERS_INFO_FEATURE_ID`
 * from `backend/src/2025-06/api-service/routes/schemas/v3/constants.js`.
 */
export const VIEW_NETWORK_USERS_INFO_FEATURE_ID = 'view-network-users-info';
